import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getProject, type Project, PERSONA_TYPE_LABELS } from '../api/projects';
import { AppLayout } from '../components/AppLayout';
import { BigButton, EmptyState, LoadingDots } from '../components/ChildFriendly';
import { useAuth } from '../auth/AuthContext';

export default function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isTeacher = user?.role === 'teacher';

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    getProject(Number(projectId))
      .then((data) => {
        if (!cancelled) setProject(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Proje detayları yüklenemedi.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (loading) {
    return (
      <AppLayout title="Proje Detayı">
        <LoadingDots message="Proje yükleniyor" />
      </AppLayout>
    );
  }

  if (error || !project) {
    return (
      <AppLayout title="Proje Detayı">
        <div role="alert" className="error">
          {error || 'Proje bulunamadı.'}
        </div>
        <BigButton variant="ghost" onClick={() => navigate(isTeacher ? '/teacher' : '/')}>
          {isTeacher ? '← Öğretmen Paneline Dön' : '← Panele Dön'}
        </BigButton>
      </AppLayout>
    );
  }

  const sessions = project.role_sessions ?? [];

  return (
    <AppLayout
      title="Proje Detayı"
      subtitle={isTeacher ? 'Öğrencinin mülakat rotasını ve muhatap görüşmelerini inceliyorsunuz.' : 'Çözümünü anlatacağın muhatapları seç ve mülakata başla.'}
      actions={
        <BigButton variant="ghost" onClick={() => navigate(isTeacher ? '/teacher' : '/')}>
          {isTeacher ? '← Öğretmen Paneline Dön' : '← Panele Dön'}
        </BigButton>
      }
    >
      <div className="stack">
        {isTeacher && (
          <div className="teacher-inspection-banner" style={{ background: '#3b82f6', color: '#ffffff', padding: '0.85rem 1.25rem', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.2)' }}>
            <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
              👀 <strong>Öğretmen İnceleme Modu:</strong> Öğrenci Projesi #{project.id}
            </span>
            <button
              type="button"
              className="btn btn--sm teacher-banner-btn"
              onClick={() => navigate('/teacher')}
            >
              ← Öğretmen Paneline Dön
            </button>
          </div>
        )}

        <div className="problem-recap" aria-label="Problemin">
          <span className="problem-recap__label">
            <span aria-hidden="true">📝</span> Proje Problemi
          </span>
          <p className="problem-recap__text">{project.problem_text}</p>
        </div>

        {project.status === 'passed' && (
          <section
            className="diploma-hero"
            data-overall="passed"
            aria-labelledby="diploma-banner-heading"
          >
            <span className="diploma-hero__emoji" aria-hidden="true">🎓</span>
            <h2 id="diploma-banner-heading" className="diploma-hero__title">
              {isTeacher ? 'Öğrenci bu projeyi geçti!' : 'Bu projeyi geçtin!'}
            </h2>
            <p className="diploma-hero__subtitle">
              {isTeacher ? 'Öğrencinin diplomasını görüntülemek için tıklayın.' : 'Diplomanı görüntülemek veya PDF olarak indirmek için aşağıdaki butona tıkla.'}
            </p>
            <div className="diploma-actions">
              <BigButton
                variant="primary"
                icon="🎓"
                onClick={() => navigate(`/projects/${project.id}/diploma`)}
              >
                Diplomayı Gör
              </BigButton>
            </div>
          </section>
        )}

        {project.status === 'failed' && (
          <section
            className="diploma-hero"
            data-overall="failed"
            aria-labelledby="failed-banner-heading"
          >
            <span className="diploma-hero__emoji" aria-hidden="true">💪</span>
            <h2 id="failed-banner-heading" className="diploma-hero__title">
              Bu proje geçilemedi
            </h2>
            <p className="diploma-hero__subtitle">
              Detaylı raporu görmek için aşağıdaki butona tıkla.
            </p>
            <div className="diploma-actions">
              <BigButton
                variant="ghost"
                onClick={() => navigate(`/projects/${project.id}/diploma`)}
              >
                Raporu Gör
              </BigButton>
            </div>
          </section>
        )}

        <section className="stack" aria-labelledby="roles-heading">
          <div>
            <h3 id="roles-heading" className="section-title">
              Mülakat Rolleri
            </h3>
            <p className="section-subtitle">
              {isTeacher ? 'Öğrencinin YZ kişileriyle gerçekleştirdiği görüşmeleri inceleyin.' : 'Konuşmak istediğin muhatabı seç ve mülakata başla.'}
            </p>
          </div>

          {sessions.length === 0 ? (
            <EmptyState
              icon="🤔"
              title="Henüz rol tanımlanmamış"
              message="Bu proje için henüz bir mülakat rolü bulunmuyor."
            />
          ) : (
            <ul className="role-grid">
              {sessions.map((rs, idx) => {
                const icon =
                  rs.persona_type && PERSONA_TYPE_LABELS[rs.persona_type]
                    ? PERSONA_TYPE_LABELS[rs.persona_type].icon
                    : '👤';
                return (
                  <li key={rs.id} className="role-card" data-status={rs.status}>
                    <div className="role-card__number" aria-hidden="true">
                      {idx + 1}
                    </div>
                    <div className="role-card__icon" aria-hidden="true">
                      {icon}
                    </div>
                    <h4 className="role-card__title">{rs.role_name}</h4>
                    <p className="role-card__focus">{rs.role_focus}</p>
                    <div className="role-card__meta">
                      <span>Durum</span>
                      <span className={`status-badge status-${rs.status}`}>
                        {roleStatusLabel(rs.status)}
                      </span>
                    </div>
                    <BigButton
                      variant={rs.status === 'passed' ? 'ghost' : 'primary'}
                      fullWidth
                      onClick={() =>
                        navigate(`/projects/${project.id}/roles/${rs.id}`)
                      }
                    >
                      {isTeacher
                        ? '💬 Görüşmeyi İncele →'
                        : rs.status === 'passed'
                        ? '↻ Tekrar Konuş'
                        : 'Görüşmeyi Başlat →'}
                    </BigButton>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </AppLayout>
  );
}

function roleStatusLabel(status: string): string {
  switch (status) {
    case 'pending': return 'Bekliyor';
    case 'active': return 'Devam Ediyor';
    case 'passed': return 'Başarılı';
    case 'failed': return 'Başarısız';
    default: return status;
  }
}
